import React, { useState, useEffect } from "react";
import { User, Mail, Phone, Lock, Check, ArrowLeft, Eye, EyeOff } from "lucide-react";
import { useDispatch, useSelector } from "react-redux";
import { registerMerchant, verifyOtpMerchant} from "../../features/merchant/merchantThunks";
import { navigateTo } from "../../utils/navigation";
import { toast } from "react-toastify";
import '../merchant/auth/auth.css';

const FormInput = ({
  icon: Icon,
  rightIcon: RightIcon,
  onRightIconClick,
  error,
  className = "",
  ...props
}) => (
  <div className="form-group">
    <div className={`input-container ${className} ${error ? "has-error" : ""}`}>
      {Icon && <Icon className="input-icon" size={18} />}

      <input className="form-input" {...props} />

      {RightIcon && (
        <span className="right-icon" onClick={onRightIconClick}>
          <RightIcon size={18} />
        </span>
      )}
    </div>

    {error && <span className="error-text">{error}</span>}
  </div>
);



const Register = () => {
  const dispatch = useDispatch();

  const { loading, error, registrationSuccess, otpVerified } = useSelector(
    (state) => state.merchant
  );

  const [currentStep, setCurrentStep] = useState(1);
const [errors, setErrors] = useState({});
const [showPassword, setShowPassword] = useState(false);


  const [formData, setFormData] = useState({
    fullName: "",
    email: "",
    mobileNumber: "",
    password: "",
    otp: "",
  });


  const validateStep = () => {
  let newErrors = {};

  if (currentStep === 1) {
    if (!formData.fullName.trim()) {
      newErrors.fullName = "Full name is required";
    } else if (formData.fullName.length < 3) {
      newErrors.fullName = "Full name must be at least 3 characters";
    }

if (formData.email.trim() && 
    !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
  newErrors.email = "Enter a valid email address";
}



    if (!formData.mobileNumber.trim()) {
      newErrors.mobileNumber = "Mobile number is required";
    } else if (!/^[0-9]{10}$/.test(formData.mobileNumber)) {
      newErrors.mobileNumber = "Enter a valid 10-digit mobile number";
    }

    if (!formData.password.trim()) {
      newErrors.password = "Password is required";
    } else if (formData.password.length < 8) {
      newErrors.password = "Password must be at least 8 characters";
    } else if (!/[A-Z]/.test(formData.password)) {
      newErrors.password = "Password must contain at least 1 uppercase letter";
    } else if (!/[a-z]/.test(formData.password)) {
      newErrors.password = "Password must contain at least 1 lowercase letter";
    } else if (!/[!@#$%^&*(),.?":{}|<>]/.test(formData.password)) {
      newErrors.password = "Password must contain at least 1 special character";
    }
  }

  if (currentStep === 2) {
    if (!formData.otp.trim()) {
      newErrors.otp = "OTP is required";
    } else if (!/^[0-9]{6}$/.test(formData.otp)) {
      newErrors.otp = "OTP must be 6 digits";
    }
  }

  setErrors(newErrors);
  return Object.keys(newErrors).length === 0;
};


  const updateFormData = (field, value) =>
    setFormData((prev) => ({ ...prev, [field]: value }));

  // STEP 1 — Register
  const handleRegister = () => {
   if (
  !formData.fullName ||
  !formData.mobileNumber ||
  !formData.password
) {
  return toast.error("Please fill all required fields");
}


    const payload = {
  fullName: formData.fullName,
  mobileNumber: formData.mobileNumber,
  password: formData.password,
  role: "user",
};

if (formData.email.trim()) {
  payload.email = formData.email;
}

dispatch(registerMerchant(payload));

  };

  // STEP 2 — Verify OTP
  const handleVerifyOtp = () => {
    if (!formData.otp || formData.otp.length !== 6) {
      return toast.error("Please enter 6-digit OTP");
    }

    dispatch(
      verifyOtpMerchant({
        mobileNumber: formData.mobileNumber,
        otp: formData.otp,
      })
    );
  };

  // Auto move to Step 2 after OTP sent
  useEffect(() => {
    if (registrationSuccess) {
      toast.success("OTP sent successfully!");
      setCurrentStep(2);
    }
  }, [registrationSuccess]);

  // Navigate after OTP verified
  useEffect(() => {
    if (otpVerified) {
      toast.success("Merchant registered successfully!");
      navigateTo("/merchant-login");
    }
  }, [otpVerified]);

  // Display API error
  useEffect(() => {
    if (error) toast.error(error);
  }, [error]);

  const handleNextStep = () => {
  if (!validateStep()) return;

  currentStep === 1 ? handleRegister() : handleVerifyOtp();
};

  const prevStep = () => setCurrentStep((p) => (p > 1 ? p - 1 : p));

  // ---------------- UI --------------------

  const renderProgressBar = (
    <div className="progress-container">
      {[
        { number: 1, label: "Account", icon: User },
        { number: 2, label: "Verify", icon: Phone },
      ].map((step, index, arr) => {
        const Icon = step.icon;
        const isCompleted = currentStep > step.number;
        const isActive = currentStep === step.number;

        return (
          <div key={step.number} className="progress-item">
            <div
              className={`progress-circle ${
                isCompleted ? "completed" : isActive ? "active" : ""
              }`}
            >
              {isCompleted ? <Check size={16} /> : <Icon size={16} />}
            </div>
            <span className={`progress-label ${isActive || isCompleted ? "active" : ""}`}>
              {step.label}
            </span>

            {index < arr.length - 1 && (
              <div className={`progress-line ${isCompleted ? "completed" : ""}`} />
            )}
          </div>
        );
      })}
    </div>
  );

  const renderStepHeader = (
    <div className="step-header">
      {currentStep === 1 ? (
        <>
          <h1 className="step-title">Create New Account</h1>
          <p className="step-subtitle">Join us today and get started</p>
        </>
      ) : (
        <>
          <h1 className="step-title">Verify Mobile</h1>
          <p className="step-subtitle">
            We sent an OTP to <b>{formData.mobileNumber}</b>
          </p>
        </>
      )}
    </div>
  );

  const renderFormStep = () =>
    currentStep === 1 ? (
      <>
        <FormInput
            icon={User}
           placeholder="Full Name"
           value={formData.fullName}
           onChange={(e) => updateFormData("fullName", e.target.value)}
          error={errors.fullName}
        />
        <FormInput
         icon={Mail}
  placeholder="Email Address ( optional )"
  value={formData.email}
  onChange={(e) => updateFormData("email", e.target.value)}
  error={errors.email}
        />
        <FormInput
          icon={Phone}
  placeholder="Mobile Number"
  maxLength="10"
  value={formData.mobileNumber}
  onChange={(e) =>
    updateFormData("mobileNumber", e.target.value.replace(/[^0-9]/g, ""))
  }
  error={errors.mobileNumber}
        />
      <FormInput
  icon={Lock}
  type={showPassword ? "text" : "password"}
  placeholder="Password"
  value={formData.password}
  onChange={(e) => updateFormData("password", e.target.value)}
  error={errors.password}
  rightIcon={showPassword ? EyeOff : Eye}
  onRightIconClick={() => setShowPassword((p) => !p)}
/>

      </>
    ) : (
      <>
        <FormInput
          icon={Phone}
          type="tel"
          readOnly
          className="mobile-readonly"
          value={formData.mobileNumber}
        />
        <FormInput
          icon={Lock}
  placeholder="Enter 6-digit OTP"
  maxLength="6"
  value={formData.otp}
  onChange={(e) =>
    updateFormData("otp", e.target.value.replace(/[^0-9]/g, ""))
  }
  error={errors.otp}
        />
      </>
    );

  return (
    <div className="register-container">
      <div className="register-card">
        {currentStep > 1 && (
          <button className="back-button" onClick={prevStep} disabled={loading}>
            <ArrowLeft size={18} />
          </button>
        )}

        {renderProgressBar}
        {renderStepHeader}

        <div className="form-content">{renderFormStep()}</div>

        <div className="button-group">
          <button
            className="primary-button"
            onClick={handleNextStep}
            disabled={loading}
          >
            {loading
              ? "Processing..."
              : currentStep === 1
              ? "Register & Send OTP"
              : "Verify OTP"}
          </button>
        </div>
            <div className="login-section">
                          Already registered ?{' '}
                          <span
                            className="login-link"
                            onClick={() => navigateTo('/merchant-login')}
                            style={{ cursor: 'pointer' }}
                          >
                            Login Now 
                          </span>
                        </div>
      </div>
    </div>
  );
};

export default Register;
